import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import Navbar from '../../shared/navbar.js';
import '../properties/PropertyAddWizard.css';

const STEPS = [
  { id: 1, title: 'Basic info', short: 'Basics' },
  { id: 2, title: 'Capacity & seniority', short: 'Role' },
  { id: 3, title: 'Contact information', short: 'Contact' },
  { id: 4, title: 'Review & create', short: 'Review' },
];

const CAPACITY_OPTIONS = ['Agent', 'Maintenance', 'Legal'];
const SENIORITY_OPTIONS = ['Junior', 'Mid-level', 'Senior', 'Lead'];

function StaffAddWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [staff, setStaff] = useState({
    name: '',
    official_title: '',
    role: 'Agent',
    seniority: '',
    email: '',
    phone: '',
    secondary_phone: '',
  });

  useEffect(() => {
    const u = localStorage.getItem('username');
    if (!u) navigate('/');
  }, [navigate]);

  const update = (key, value) => setStaff(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    setError('');
    if (!staff.name?.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/staff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: staff.name.trim(),
          official_title: (staff.official_title || '').trim(),
          role: staff.role || 'Agent',
          seniority: (staff.seniority || '').trim(),
          email: (staff.email || '').trim(),
          phone: (staff.phone || '').trim(),
          secondary_phone: (staff.secondary_phone || '').trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create staff');
      navigate('/databases');
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar selected="Databases" />
      <div className="add-property-flow">
        <div className="add-property-flow__page">
          <div className="add-property-flow__card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <h1 className="add-property-flow__title" style={{ margin: 0 }}>Add Staff Member</h1>
              <button type="button" className="add-property-flow__close" aria-label="Close" onClick={() => navigate('/databases')}>×</button>
            </div>
            <div className="add-property-flow__steps" role="tablist">
              {STEPS.map(s => (
                <button key={s.id} type="button" className={`add-property-flow__step-tab ${step === s.id ? 'add-property-flow__step-tab--active' : ''}`} onClick={() => setStep(s.id)}>
                  {s.id}. {s.short}
                </button>
              ))}
            </div>
            {error && <div className="add-property-flow__error">{error}</div>}

            {step === 1 && (
              <div className="add-property-flow__panel">
                <h2>Basic information</h2>
                <p className="add-property-flow__hint">Name and official title.</p>
                <div className="add-property-flow__grid">
                  <div className="add-property-flow__field add-property-flow__field--full">
                    <label>Full name *</label>
                    <input value={staff.name} onChange={e => update('name', e.target.value)} placeholder="e.g. Jane Smith" />
                  </div>
                  <div className="add-property-flow__field add-property-flow__field--full">
                    <label>Official title</label>
                    <input value={staff.official_title} onChange={e => update('official_title', e.target.value)} placeholder="e.g. Property Manager, Leasing Agent, Maintenance Supervisor" />
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="add-property-flow__panel">
                <h2>Capacity & seniority</h2>
                <p className="add-property-flow__hint">What capacity they serve and their seniority level.</p>
                <div className="add-property-flow__grid">
                  <div className="add-property-flow__field">
                    <label>Capacity (role type)</label>
                    <select value={staff.role} onChange={e => update('role', e.target.value)}>
                      {CAPACITY_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="add-property-flow__field">
                    <label>Seniority</label>
                    <select value={staff.seniority} onChange={e => update('seniority', e.target.value)}>
                      <option value="">Select</option>
                      {SENIORITY_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="add-property-flow__panel">
                <h2>Contact information</h2>
                <p className="add-property-flow__hint">Primary and optional secondary contact details.</p>
                <div className="add-property-flow__grid">
                  <div className="add-property-flow__field add-property-flow__field--full">
                    <label>Email</label>
                    <input type="email" value={staff.email} onChange={e => update('email', e.target.value)} placeholder="email@example.com" />
                  </div>
                  <div className="add-property-flow__field">
                    <label>Phone</label>
                    <input value={staff.phone} onChange={e => update('phone', e.target.value)} placeholder="(555) 555-5555" />
                  </div>
                  <div className="add-property-flow__field">
                    <label>Secondary phone</label>
                    <input value={staff.secondary_phone} onChange={e => update('secondary_phone', e.target.value)} placeholder="Optional" />
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="add-property-flow__panel">
                <h2>Review & create</h2>
                <div className="add-property-flow__review">
                  <section>
                    <h3>Name & title</h3>
                    <p><strong>{staff.name || '—'}</strong></p>
                    <p>{staff.official_title || '—'}</p>
                  </section>
                  <section>
                    <h3>Capacity</h3>
                    <p>Role: {staff.role || '—'} · Seniority: {staff.seniority || '—'}</p>
                  </section>
                  <section>
                    <h3>Contact</h3>
                    <p>Email: {staff.email || '—'}</p>
                    <p>Phone: {staff.phone || '—'}</p>
                    {staff.secondary_phone && <p>Secondary: {staff.secondary_phone}</p>}
                  </section>
                </div>
              </div>
            )}

            <div className="add-property-flow__actions">
              {step > 1 && <button type="button" className="add-property-flow__btn add-property-flow__btn--secondary" onClick={() => setStep(step - 1)}>Back</button>}
              {step < 4 && <button type="button" className="add-property-flow__btn add-property-flow__btn--primary" onClick={() => setStep(step + 1)}>Next</button>}
              {step === 4 && (
                <button type="button" className="add-property-flow__btn add-property-flow__btn--primary" disabled={saving} onClick={handleSubmit}>
                  {saving ? 'Creating…' : 'Create staff member'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default StaffAddWizard;
